/**
 * Reproduce el dictado grabado de un estudio y ofrece transcribirlo.
 *
 * El motor devuelve el texto, no lo guarda: quien transcribe lo revisa antes
 * de que entre al informe.
 *
 * @module presentation/components/dictation/DictadoGrabado
 */

import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Loader2, Mic, Sparkles } from 'lucide-react'

import { useStudyAttachments } from '@/application/study-attachments'
import { attachmentService } from '@/application/study-attachments/attachment.service'
import { getErrorMessage } from '@/lib/api'
import { toast } from '@/lib/toast'
import { Button } from '@/presentation/components/ui/Button'

interface Props {
  studyId: string
  /** Recibe el texto del motor para que la pantalla decida donde ponerlo. */
  onTexto: (texto: string) => void
}

export function DictadoGrabado({ studyId, onTexto }: Props) {
  const { data } = useStudyAttachments(studyId)
  const [fuente, setFuente] = useState<string | null>(null)
  const [transcrito, setTranscrito] = useState(false)

  const dictado = data?.attachments.find((a) => a.attachmentType === 'DICTATION_AUDIO')
  const dictadoId = dictado?.id

  useEffect(() => {
    if (!dictadoId) return

    let vigente = true
    let url: string | null = null

    attachmentService
      .download(studyId, dictadoId)
      .then((blob) => {
        url = URL.createObjectURL(blob)
        if (vigente) setFuente(url)
      })
      .catch(() => {
        if (vigente) toast.error('No se pudo cargar el audio del dictado')
      })

    return () => {
      vigente = false
      if (url) URL.revokeObjectURL(url)
    }
  }, [studyId, dictadoId])

  const transcribir = useMutation({
    mutationFn: () => attachmentService.transcribir(studyId, dictadoId!),
    onSuccess: ({ text }) => {
      if (!text.trim()) {
        toast.error('El motor no devolvió texto. Escuche el audio y transcriba a mano')
        return
      }
      onTexto(text)
      setTranscrito(true)
    },
    onError: (err) => toast.error(getErrorMessage(err, 'No se pudo transcribir el dictado')),
  })

  if (!dictado) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4 text-center bg-[var(--background-subtle)] rounded-lg">
        <Mic className="h-12 w-12 text-[var(--foreground-muted)] mb-4" />
        <p className="text-[var(--foreground-muted)] font-medium">
          El especialista no dejó dictado grabado
        </p>
        <p className="text-xs text-[var(--foreground-muted)] mt-2">
          Escriba el informe con el micrófono de cada campo.
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--background-subtle)] p-3 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-[var(--foreground)]">Dictado del especialista</p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => transcribir.mutate()}
          disabled={transcribir.isPending}
          title="El motor propone el texto; usted lo corrige"
        >
          {transcribir.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4 mr-2" />
          )}
          {transcrito ? 'Transcribir de nuevo' : 'Transcribir con el motor'}
        </Button>
      </div>

      {fuente ? (
        <audio controls src={fuente} className="w-full" />
      ) : (
        <p className="text-xs text-[var(--foreground-muted)]">Cargando el audio…</p>
      )}

      <p className="text-xs text-[var(--foreground-muted)]">
        Escúchelo y escriba, o pida el texto al motor y corríjalo. El audio queda
        guardado para poder volver a lo que se dijo.
      </p>
    </div>
  )
}
